import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const isValidGaId = (id: string) => /^G-[A-Z0-9]+$/i.test(id);

export const GOOGLE_ANALYTICS_ID_KEY = "google_analytics_id";

/**
 * Reads the admin-configured GA4 measurement ID from `admin_settings`
 * and injects the gtag.js loader + config into <head> at runtime.
 *
 * The measurement ID is a public client-side identifier, so it lives in
 * plain `admin_settings` (same pattern as the Featured/How-It-Works settings)
 * and is editable from Admin → Theme Options. Leaving it blank disables tracking.
 */
export function useGoogleAnalyticsId() {
  return useQuery({
    queryKey: ["google-analytics-id"],
    queryFn: async (): Promise<string> => {
      const { data, error } = await supabase
        .from("admin_settings")
        .select("value")
        .eq("key", GOOGLE_ANALYTICS_ID_KEY)
        .maybeSingle();
      if (error) throw error;
      return (data?.value ?? "").trim();
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useGoogleAnalytics() {
  const { data: measurementId } = useGoogleAnalyticsId();
  const location = useLocation();

  // Inject the gtag.js loader + init once. `send_page_view: false` defers the
  // initial pageview to the route-change effect below so SPA navigations and the
  // first load are all tracked through one path (no double count on first load).
  useEffect(() => {
    if (!measurementId) return;
    if (!isValidGaId(measurementId)) return;
    if (document.getElementById("ga-gtag-src")) return;

    const loader = document.createElement("script");
    loader.id = "ga-gtag-src";
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(loader);

    const inline = document.createElement("script");
    inline.id = "ga-gtag-init";
    inline.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}', { send_page_view: false });
    `;
    document.head.appendChild(inline);
  }, [measurementId]);

  // Send an explicit page_view on every route change (including first load),
  // since client-side navigations never reload the page. Stays disabled when no
  // valid ID is configured.
  useEffect(() => {
    if (!measurementId || !isValidGaId(measurementId)) return;
    if (typeof window.gtag !== "function") return;

    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [measurementId, location.pathname, location.search]);
}

